"use client";

import { useState, useEffect } from "react";
import { VSL_CONFIG, QUIZ_TIMERS } from "@/config/constants";
import { VturbPlayer } from "@/components/VturbPlayer";
import { Button } from "@/components/ui/button";

type VSLVersion = "marlon" | "camilla";

interface Hero8PilaresProps {
  onCtaClick?: () => void;
}

export const Hero8Pilares = ({ onCtaClick }: Hero8PilaresProps) => {
  const [mounted, setMounted] = useState(false);
  const [vslVersion, setVslVersion] = useState<VSLVersion>("marlon");
  const [showButton, setShowButton] = useState(false);

  const vslConfig = VSL_CONFIG.metodolda;

  useEffect(() => {
    setMounted(true);
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) {
      document.documentElement.classList.add("reduced-motion");
    }

    // Verificar query parameter
    const searchParams = new URLSearchParams(window.location.search);
    const vslParam = searchParams.get("vsl") as VSLVersion | null;
    if (vslParam && (vslParam === "marlon" || vslParam === "camilla")) {
      setVslVersion(vslParam);
    }
  }, []);

  // Timer para mostrar botão
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButton(true);
    }, QUIZ_TIMERS.product_page_metodolda);
    return () => clearTimeout(timer);
  }, []);

  if (!mounted) return null;

  return (
    <section
      className="relative flex overflow-hidden"
      aria-label="Seção principal com VSL do Método LDA"
    >
      {/* Background animado (respeita prefers-reduced-motion) */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
        <div
          className="absolute top-20 left-20 w-96 h-96 bg-primary/30 rounded-full blur-[100px] motion-safe:animate-glow-pulse reduced-motion:opacity-75"
          aria-hidden
        />
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-[100px] motion-safe:animate-glow-pulse reduced-motion:opacity-75"
          style={{ animationDelay: "2s" }}
          aria-hidden
        />
      </div>

      {/* Conteúdo central */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl flex flex-col items-center gap-8 sm:gap-10">
          {/* Título chamativo acima da VSL */}
          <header className="w-full text-center space-y-4">
            <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-4xl font-black leading-[1.05] tracking-tight">
              COMO PERDER A VONTADE DE APOSTAR AINDA<br/>
              <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
                HOJE USANDO APENAS 3 TÉCNICAS SIMPLES
              </span>
            </h1>
          </header>

          {/* VSL: ConvertKit Player */}
          <div className="relative w-full px-0">
            {/* Glow de fundo do card */}
            <div
              className="pointer-events-none absolute inset-0 -z-10 bg-gradient-radial from-primary/40 via-accent/20 to-transparent blur-3xl"
              aria-hidden
            />

            <div className="mx-auto w-full max-w-full sm:max-w-[1200px] lg:max-w-[1400px] group motion-safe:animate-scale-in px-0 sm:px-2">
              <div className="relative rounded-2xl sm:rounded-3xl p-0.5 sm:p-1.5 bg-gradient-to-br from-primary/30 via-card/20 to-accent/30 overflow-hidden">
                <VturbPlayer
                  key={vslVersion}
                  videoId={vslConfig[vslVersion].id}
                  scriptUrl={vslConfig[vslVersion].url}
                  className="relative aspect-video w-full rounded-[0.75rem] sm:rounded-[1.25rem]"
                />
              </div>

              {/* Glow ao redor no hover */}
              <div className="pointer-events-none absolute -inset-4 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-radial from-primary/25 via-accent/10 to-transparent blur-3xl" />
            </div>

            {/* Botão "Quero Comprar" que aparece após 20 segundos */}
            {showButton && (
              <div className="mt-8 flex justify-center animate-fade-in">
                <Button
                  variant="hero"
                  size="xl"
                  onClick={onCtaClick}
                  className="px-12 w-[100%]"
                >
                  🚀 QUERO ME LIBERTAR
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sombra de base */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};
